/**
 * File type detection and utility functions
 */

export type FileType =
  | "image"
  | "pdf"
  | "document"
  | "video"
  | "audio"
  | "unknown";

const imageExtensions = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
  "svg",
  "ico",
  "heic",
  "heif",
  "avif",
]);

const videoExtensions = new Set([
  "mp4",
  "avi",
  "mov",
  "wmv",
  "flv",
  "webm",
  "mkv",
]);

const documentExtensions = new Set([
  "doc",
  "docx",
  "txt",
  "rtf",
  "odt",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "csv",
]);

const audioExtensions = new Set(["mp3", "wav", "ogg", "aac", "flac", "m4a"]);

/**
 * Get file type based on file extension
 */
export const getFileType = (fileName: string): FileType => {
  const extension = fileName.toLowerCase().split(".").pop();

  if (!extension) return "unknown";

  if (imageExtensions.has(extension)) return "image";

  if (extension === "pdf") return "pdf";

  if (videoExtensions.has(extension)) return "video";

  if (audioExtensions.has(extension)) return "audio";

  if (documentExtensions.has(extension)) return "document";

  return "unknown";
};

/**
 * Check if file can be previewed inline
 */
export const canPreviewFile = (fileName: string): boolean => {
  const fileType = getFileType(fileName);
  return fileType === "image" || fileType === "pdf";
};

/**
 * Get MIME type from file extension
 */
export const getMimeType = (fileName: string): string => {
  const extension = fileName.toLowerCase().split(".").pop();

  if (!extension) return "application/octet-stream";

  const mimeTypes: { [key: string]: string } = {
    // Images
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    bmp: "image/bmp",
    webp: "image/webp",
    svg: "image/svg+xml",
    ico: "image/x-icon",
    heic: "image/heic",
    heif: "image/heif",
    avif: "image/avif",

    // PDF
    pdf: "application/pdf",

    // Documents
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    txt: "text/plain",
    rtf: "application/rtf",
    odt: "application/vnd.oasis.opendocument.text",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    csv: "text/csv",

    // Video
    mp4: "video/mp4",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    webm: "video/webm",
    mkv: "video/x-matroska",

    // Audio
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    aac: "audio/aac",
    flac: "audio/flac",
    m4a: "audio/mp4",
  };

  return mimeTypes[extension] || "application/octet-stream";
};
